import { useCallback } from "react";
import { DefaultValues, FieldValues, UseFormReturn } from "react-hook-form";
import { ICategorizableItem } from "../models/categorizedNomenclatureModels";

interface UseItemFormHandlersParams<
  TFormModel extends FieldValues & ICategorizableItem,
  TItemModel extends ICategorizableItem
> {
  form: UseFormReturn<TFormModel>;
  defaultValues: () => DefaultValues<TFormModel>;
  setItemDialogOpen: (open: boolean) => void;
  setEditingItemId: (id: number | null) => void;
  createItem: (data: Partial<TItemModel>) => void;
  updateItem: (id: number, data: Partial<TItemModel>) => void;
  transformPayload?: (data: TFormModel) => Partial<TItemModel>;
  editOnRowClick: boolean;
  userHavePermissions: (endpoint: string) => boolean;
  updateEndpoint: string;
}

export function useItemFormHandlers<
  TFormModel extends FieldValues & ICategorizableItem,
  TItemModel extends ICategorizableItem
>({
  form,
  defaultValues,
  setItemDialogOpen,
  setEditingItemId,
  createItem,
  updateItem,
  transformPayload,
  editOnRowClick,
  userHavePermissions,
  updateEndpoint,
}: UseItemFormHandlersParams<TFormModel, TItemModel>) {
  const onEditItemClick = useCallback(
    (itemId?: number, categoryId?: number | null) => {
      setEditingItemId(itemId ?? null);
      const defaults = defaultValues();
      if (categoryId !== undefined) {
        (defaults as any).categoryId = categoryId;
      }
      form.reset(defaults);
      setItemDialogOpen(true);
    },
    [defaultValues, form, setEditingItemId, setItemDialogOpen]
  );

  const onHideItemDialog = useCallback(() => {
    form.reset(defaultValues());
    setItemDialogOpen(false);
    setEditingItemId(null);
  }, [defaultValues, form, setItemDialogOpen, setEditingItemId]);

  const onSubmitItem = useCallback(
    async (data: TFormModel) => {
      const payload = transformPayload
        ? transformPayload(data)
        : (data as unknown as Partial<TItemModel>);
      if (data.id) {
        updateItem(data.id, payload);
      } else {
        createItem(payload);
      }
    },
    [createItem, transformPayload, updateItem]
  );

  const handleItemRowClick = useCallback(
    (item: TItemModel | undefined) => {
      const canEdit = userHavePermissions(updateEndpoint);
      if (editOnRowClick && item?.id && !item.isDeleted && canEdit) {
        onEditItemClick(item.id, item.categoryId);
      }
    },
    [editOnRowClick, updateEndpoint, onEditItemClick, userHavePermissions]
  );

  return {
    onEditItemClick,
    onHideItemDialog,
    onSubmitItem,
    handleItemRowClick,
  };
}
